import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useContext, useEffect, useRef, useState } from 'react'
import { Breadcrumbs, Button, IconButton, InputAdornment, LinearProgress, ListItemButton, TextField, Typography } from '@mui/material'

import { countriesCurrencies } from '../lib'
import { useSocket } from '../hooks/use-socket'
import HelpIcon from '../assets/icons/help-icon.svg'
import { requestAccessToken } from '../api/auth.api'
import NextIcon from '../assets/icons/next-icon.svg'
import SearchIcon from '../assets/icons/search-icon.svg'
import Pagination from '../components/common/pagination'
import { searchPaymentsByRegex } from '../api/app-queries.api'
import { CustomerCompany, FormError, Payment } from '../types'
import { getPaymentsPendingRoutingData } from '../api/app-data.api'
import { AppStateContext, AuthContext, UserContext } from '../contexts'
import { baseButtonBlueBg, textFieldWhiteBg } from '../theme/custom-styles'
import { handleIsValidMatch, upsertManyWithLimitAtStartFast } from '../utils/common.utils'
import DocumentPaymentListItem from '../components/document-payments-details-management/document-payment-list-item'
import { upsertPaymentWithLimitAtStart } from '../utils'

const PaymentsPendingRoutingManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { customerCompanyId } = useParams()
  const currentListPageRef = useRef<number>(1)
  const paymentToSearchRef = useRef<string>('')
  const { userSession } = useContext(UserContext)
  const currentLoadingDataRef = useRef<boolean>(true)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { setScrollToTop } = useContext(AppStateContext)
  const selectedMainFilterRef = useRef<string>('pending')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const paymentsDisplayedDataRef = useRef<Array<Payment>>([])
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { t } = useTranslation(['customer-company-details-management'])
  const currentPaymentsPendingRoutingDataRef = useRef<Array<Payment>>([])

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [hasMultipleRows, setHasMultipleRows] = useState(false)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [paymentToSearch, setPaymentToSearch] = useState<string>('')
  const [paymentsData, setPaymentsData] = useState<Array<Payment>>([])
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [paymentsListData, setPaymentsListData] = useState<Array<Payment>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [selectedMainFilter, setSelectedMainFilter] = useState<string>('pending')
  const [searchedPaymentsData, setSearchedPaymentsData] = useState<Array<Payment>>([])
  const [paymentsDisplayedData, setPaymentsDisplayedData] = useState<Array<Payment>>([])
  const [noRegistriesFoundAtLocal, setNoRegistriesFoundAtLocal] = useState<boolean>(false)
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [customerCompanyData, setCustomerCompanyData] = useState<CustomerCompany | null>(null)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on(`paymentsPendingRouting:${selectedCurrency}`, async (dataFromSocket: any) => {
      if (dataFromSocket.deleted === true) {
        handleDeleteAndSetPayments(String(dataFromSocket._id))
        return
      }

      handleUpdateAndSetPayments(dataFromSocket)
    })

    appDataSocket.on(`paymentsPendingRoutingDeleted:${selectedCurrency}`, async (dataFromSocket: any) => {
      handleDeleteAndSetPayments(String(dataFromSocket.paymentId))
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    if (!userSession) {
      return
    }

    if (userSession && userSession?.role !== 'super-admin') {
      navigate('/404')
      return
    }

    handleGetPaymentsPendingRouting()

    const checkRows = () => {
      if (!containerRef.current) return

      const container = containerRef.current
      const firstItem = container.firstElementChild
      if (!firstItem) return

      const firstItemTop = firstItem.getBoundingClientRect().top
      const multipleRows = Array.from(container.children).some((item) => item.getBoundingClientRect().top > firstItemTop)

      setHasMultipleRows(multipleRows)
    }

    const resizeObserver = new ResizeObserver(checkRows)

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = paymentToSearchRef.current === '' ? paymentsDisplayedData.slice(firstPageIndex, lastPageIndex) : searchedPaymentsData.slice(firstPageIndex, lastPageIndex)

    setPaymentsListData(paymentsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, /* paymentsData, */ searchedPaymentsData, paymentsDisplayedData])

  useEffect(() => {
    handleSetPaymentToSearch('', false)

    const paymentsToDisplay =
      selectedMainFilterRef.current === 'pending'
        ? paymentsData.filter((paymentData: Payment) => !paymentData.routingData || (paymentData.routingData && paymentData.routingData.approved === false && paymentData.routingData.rejected === false))
        : paymentsData.filter((paymentData: Payment) => paymentData.routingData && (paymentData.routingData.approved === true || paymentData.routingData.rejected === true))

    paymentsToDisplay.sort((thisPayment: Partial<Payment>, nextPayment: Partial<Payment>) => {
      const thisCreatedAt = thisPayment.createdAt instanceof Date ? thisPayment.createdAt.toISOString() : thisPayment.createdAt
      const nextCreatedAt = nextPayment.createdAt instanceof Date ? nextPayment.createdAt.toISOString() : nextPayment.createdAt

      if (thisCreatedAt && nextCreatedAt) {
        return Date.parse(nextCreatedAt) - Date.parse(thisCreatedAt)
      }

      return 0
    })

    setPaymentsDisplayedData(paymentsToDisplay)
    paymentsDisplayedDataRef.current = paymentsToDisplay
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMainFilter])

  const handleDeleteAndSetPayments = (paymentId: string) => {
    const localPaymentsPendingRouting = window.sessionStorage.getItem(`paymentsPendingRouting:${customerCompanyId}:${selectedCountry}`) || JSON.stringify({ paymentsPendingRouting: [] })
    const paymentsDataParsed = JSON.parse(localPaymentsPendingRouting)
    const paymentsDataUpdated = paymentsDataParsed.paymentsPendingRouting.filter((paymentData: Payment) => String(paymentData._id) !== String(paymentId))

    handleSetInitialData({ ...paymentsDataParsed, paymentsPendingRouting: paymentsDataUpdated })
  }

  const handleUpdateAndSetPayments = (dataFromSocket: any) => {
    const localPaymentsPendingRouting = window.sessionStorage.getItem(`paymentsPendingRouting:${customerCompanyId}:${selectedCountry}`) || JSON.stringify({ paymentsPendingRouting: [] })
    const paymentsDataParsed = JSON.parse(localPaymentsPendingRouting)
    const paymentsDataUpdated = upsertPaymentWithLimitAtStart(paymentsDataParsed.paymentsPendingRouting, dataFromSocket, 500)

    handleSetInitialData({ ...paymentsDataParsed, paymentsPendingRouting: paymentsDataUpdated })
  }

  const handleGetPaymentsPendingRouting = async () => {
    let localPaymentsPendingRoutingToSet: any = {}
    const localPaymentsPendingRouting = window.sessionStorage.getItem(`paymentsPendingRouting:${customerCompanyId}:${selectedCountry}`)

    if (localPaymentsPendingRouting) {
      localPaymentsPendingRoutingToSet = JSON.parse(localPaymentsPendingRouting)
      handleSetInitialData(localPaymentsPendingRoutingToSet)
    }

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentsPendingRoutingData(String(customerCompanyId), selectedCountry, accessToken)

      handleSetInitialData(data)
      window.sessionStorage.setItem(`paymentsPendingRouting:${customerCompanyId}:${selectedCountry}`, JSON.stringify(data))
    } catch (error: any) {
      console.log('Error handleGetPaymentsPendingRouting > ', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    setCustomerCompanyData({ ...dataToSet.customerCompanyData })

    const paymentsToDisplay =
      selectedMainFilterRef.current === 'pending'
        ? dataToSet.paymentsPendingRouting.filter(
            (paymentData: Payment) => !paymentData.routingData || (paymentData.routingData && paymentData.routingData.approved === false && paymentData.routingData.rejected === false)
          )
        : dataToSet.paymentsPendingRouting.filter((paymentData: Payment) => paymentData.routingData && (paymentData.routingData.approved === true || paymentData.routingData.rejected === true))

    paymentsToDisplay.sort((thisPayment: Partial<Payment>, nextPayment: Partial<Payment>) => {
      const thisCreatedAt = thisPayment.createdAt instanceof Date ? thisPayment.createdAt.toISOString() : thisPayment.createdAt
      const nextCreatedAt = nextPayment.createdAt instanceof Date ? nextPayment.createdAt.toISOString() : nextPayment.createdAt

      if (thisCreatedAt && nextCreatedAt) {
        return Date.parse(nextCreatedAt) - Date.parse(thisCreatedAt)
      }

      return 0
    })

    if (paymentToSearchRef.current === '' || paymentToSearchRef.current === null) {
      const currentListPageDataState = currentListPageRef.current
      const firstPageIndex = currentListPageDataState !== null ? (currentListPageDataState - 1) * pageSize : 0
      const lastPageIndex = firstPageIndex + pageSize
      const paymentsPerPage = paymentsToDisplay.slice(firstPageIndex, lastPageIndex)

      setPaymentsListData(paymentsPerPage)
    }

    currentPaymentsPendingRoutingDataRef.current = dataToSet.paymentsPendingRouting
    setPaymentsData(dataToSet.paymentsPendingRouting)
    paymentsDisplayedDataRef.current = paymentsToDisplay
    setPaymentsDisplayedData(paymentsToDisplay)

    if (currentLoadingDataRef.current === true) {
      setTimeout(() => {
        currentLoadingDataRef.current = false
        setLoadingLocalData(false)
      }, 1000)
    }
  }

  const handleSetPaymentToSearch = (paymentToSearch: string, showError: boolean) => {
    currentListPageRef.current = 1
    paymentToSearchRef.current = paymentToSearch

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setErrors({})
    setCurrentListPage(1)
    setNoRegistriesFoundAtLocal(false)
    setPaymentToSearch(paymentToSearch)

    if (paymentToSearch === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentPaymentsDisplayedData = paymentsDisplayedDataRef.current ?? []
      const paymentsPerPage = currentPaymentsDisplayedData.slice(firstPageIndex, lastPageIndex)

      setPaymentsListData(paymentsPerPage)
      setSearchedPaymentsData([])

      if (showError) setErrors({ paymentToSearch: t('searchField.errors.emptyField', { ns: 'payments-to-review-management' }) })
      return
    }

    const currentFullPaymentsData = [...(paymentsDisplayedDataRef.current as Array<Payment>)]
    const filteredPaymentsHistory = currentFullPaymentsData.filter((paymentData) => {
      const paymentDateText = format(new Date(paymentData.createdAt), 'dd/MM/yyyy | HH:mm:ss')
      const paymentStatusText =
        paymentData.status === 'validated'
          ? t('paymentStatus.validated', { ns: 'customer-details-management' })
          : paymentData.status === 'reviewed'
            ? t('paymentStatus.reviewed', { ns: 'customer-details-management' })
            : paymentData.status === 'for-review'
              ? t('paymentStatus.forReview', { ns: 'customer-details-management' })
              : paymentData.status === 'waiting-for-resolution'
                ? t('paymentStatus.waitingForResolution', { ns: 'customer-details-management' })
                : paymentData.status === 'register-disperse-completed' || paymentData.status === 'processing'
                  ? t('paymentStatus.processing', { ns: 'customer-details-management' })
                  : t('paymentStatus.paid', { ns: 'customer-details-management' })

      const paymentDate = handleIsValidMatch(paymentToSearch.toLowerCase(), String(paymentDateText).toLowerCase())
      const paymentStatus = handleIsValidMatch(paymentToSearch.toLowerCase(), String(paymentStatusText).toLowerCase())
      const paymentReference = handleIsValidMatch(paymentToSearch.toLowerCase(), String(paymentData.referenceNumber).toLowerCase())
      const paymentBeneficiary = handleIsValidMatch(paymentToSearch.toLowerCase(), String(paymentData.customerRegisteredFullName).toLowerCase())

      return paymentReference || paymentBeneficiary || paymentStatus || paymentDate
    })

    const firstPageIndex = (currentListPageRef.current - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = filteredPaymentsHistory.slice(firstPageIndex, lastPageIndex)

    setPaymentsListData(paymentsPerPage)
    setSearchedPaymentsData(filteredPaymentsHistory)

    if (filteredPaymentsHistory.length === 0) {
      setNoRegistriesFoundAtLocal(true)
    }
  }

  const handleSearchPaymentsToReviewDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)

      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchPaymentsByRegex({ searchText: paymentToSearch, status: 'pending-routing', belongsTo: String(customerCompanyData?._id), selectedCountry, selectedCurrency }, accessToken)

      if (data.paymentsData.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */
      const paymentsToDisplay =
        selectedMainFilterRef.current === 'pending'
          ? data.paymentsData.filter((paymentData: Payment) => !paymentData.routingData || (paymentData.routingData && paymentData.routingData.approved === false && paymentData.routingData.rejected === false))
          : data.paymentsData.filter((paymentData: Payment) => paymentData.routingData && (paymentData.routingData.approved === true || paymentData.routingData.rejected === true))

      paymentsToDisplay.sort((thisPayment: Partial<Payment>, nextPayment: Partial<Payment>) => {
        const thisCreatedAt = thisPayment.createdAt instanceof Date ? thisPayment.createdAt.toISOString() : thisPayment.createdAt
        const nextCreatedAt = nextPayment.createdAt instanceof Date ? nextPayment.createdAt.toISOString() : nextPayment.createdAt

        if (thisCreatedAt && nextCreatedAt) {
          return Date.parse(nextCreatedAt) - Date.parse(thisCreatedAt)
        }

        return 0
      })

      setSearchedPaymentsData(paymentsToDisplay)

      const currentPaymentsDisplayedData = [...paymentsDisplayedDataRef.current]
      const paymentsDisplayedDataUpdated = currentPaymentsDisplayedData.concat(paymentsToDisplay)
      paymentsDisplayedDataUpdated.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })

      paymentsDisplayedDataRef.current = paymentsDisplayedDataUpdated
      setPaymentsDisplayedData(paymentsDisplayedDataUpdated)
      // /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */

      const currentPaymentsData = [...currentPaymentsPendingRoutingDataRef.current]
      const paymentsDataUpdated = upsertManyWithLimitAtStartFast(currentPaymentsData, data.paymentsData, 1000)
      paymentsDataUpdated.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })

      currentPaymentsPendingRoutingDataRef.current = paymentsDataUpdated
      setPaymentsData(paymentsDataUpdated)

      setSearchingInDatabase(false)
      setNoRegistriesFoundAtLocal(false)

      let localPaymentsPendingRoutingToSet: any = {}
      const localPaymentsPendingRouting = window.sessionStorage.getItem(`paymentsPendingRouting:${customerCompanyId}:${selectedCountry}`)

      if (localPaymentsPendingRouting) {
        localPaymentsPendingRoutingToSet = JSON.parse(localPaymentsPendingRouting)
        globalThis.sessionStorage.setItem(`paymentsPendingRouting:${customerCompanyId}:${selectedCountry}`, JSON.stringify({ ...localPaymentsPendingRoutingToSet, paymentsPendingRouting: paymentsDataUpdated }))
      } else {
        globalThis.sessionStorage.setItem(`paymentsPendingRouting:${customerCompanyId}:${selectedCountry}`, JSON.stringify({ paymentsPendingRouting: paymentsDataUpdated }))
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        sx={{ width: '100%' }}
        aria-label="breadcrumb"
      >
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate('/customer-companies')}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        </ListItemButton>
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate(`/customer-company-details/${customerCompanyId}`)}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.secondLevel')}
          </Typography>
        </ListItemButton>
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.pendingRouting')}
        </Typography>
      </Breadcrumbs>
      <div
        ref={containerRef}
        className={`pockets-submenu-container ${hasMultipleRows ? 'has-multiple-rows' : ''}`}
      >
        <div
          onClick={() => {
            selectedMainFilterRef.current = 'pending'
            setSelectedMainFilter('pending')
          }}
          className={`button-item ${String(selectedMainFilter) === 'pending' ? 'button-item-selected' : ''}`}
        >
          {t('filters.pending', { ns: 'reports-management' })?.toUpperCase()}
        </div>
        <div
          onClick={() => {
            selectedMainFilterRef.current = 'reviewed'
            setSelectedMainFilter('reviewed')
          }}
          className={`button-item ${String(selectedMainFilter) === 'reviewed' ? 'button-item-selected' : ''}`}
        >
          {t('filters.reviewed', { ns: 'reports-management' })?.toUpperCase()}
        </div>
      </div>
      <div
        style={{ marginTop: '20px' }}
        className="customer-company-fundings-history-container"
      >
        <div className="actions-bar">
          <div className="left-content">
            <div className="title">{paymentsDisplayedData.length === 0 ? t('actionsBar.thereIsNoPaymentsToShow') : paymentsDisplayedData.length > 0 && t('actionsBar.moneyMovementsHistory')}</div>
          </div>
          <TextField
            variant="standard"
            sx={textFieldWhiteBg}
            label={t('searchField.paymentPlaceholder')}
            value={paymentToSearch}
            disabled={searchingInDatabase}
            error={!!errors.paymentToSearch}
            helperText={errors.paymentToSearch}
            className="search-input"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleSetPaymentToSearch(paymentToSearch, true)
              }
            }}
            onChange={(event) => handleSetPaymentToSearch(event.target.value, true)}
            onBlur={() => {
              if (paymentToSearch === '') setErrors({})
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label="toggle password visibility"
                    sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                  >
                    <img
                      src={SearchIcon}
                      alt="search-icon"
                    />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </div>
      </div>
      <div className="table-header-container">
        <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg table-header-text">{t('tableHeaders.date')}</div>
        <div className="customer-company-table-column customer-company-table-width-45 table-header-text">{t('tableHeaders.beneficiary')}</div>
        <div className="customer-company-table-column customer-company-table-width-15 table-header-text customer-company-display-none-lg">{t('tableHeaders.amount')}</div>
        <div className="customer-company-table-column customer-company-table-width-20 table-header-text customer-company-display-none-sm">{t('tableHeaders.referenceNumber')}</div>
        <div className="customer-company-table-column customer-company-table-width-10 table-header-text">{t('tableHeaders.status')}</div>
      </div>
      {loadingLocalData ? (
        <div className="users-management-users-list-container">
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="documents-list-container">
            {paymentsListData.map((paymentData: Payment, index: number) => {
              return (
                <DocumentPaymentListItem
                  key={index}
                  showDateData
                  showCustomerData
                  paymentData={paymentData}
                  handleOpenPaymentDetailsModal={(selectedPaymentData: Partial<Payment>) => {
                    navigate(`/payment-pending-routing-details/${String(selectedPaymentData._id)}/${customerCompanyId}`)
                  }}
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              setTimeout(() => {
                setScrollToTop(true)
                setCurrentListPage(page)
                currentListPageRef.current = page
              }, 100)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={paymentToSearchRef.current !== '' ? searchedPaymentsData.length : paymentsDisplayedData.length}
          />
          {noRegistriesFoundAtLocal && (
            <div className="customer-no-data-found-container">
              {searchingInDatabase ? (
                <>
                  <div className="customer-no-data-found-searching-progress-bar">
                    <LinearProgress />
                  </div>
                  <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3', { ns: 'customer-company-details-management' })}</div>
                </>
              ) : (
                <>
                  {noRegistriesFoundAtDatabase === false ? (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        {t('searchingResultTexts.case1.firstPart', { ns: 'customer-company-details-management' })}{' '}
                        <strong
                          className="customer-no-data-found-no-registries-text-click"
                          onClick={() => handleSearchPaymentsToReviewDataByRegex()}
                        >
                          {t('searchingResultTexts.case1.thirdPart', { ns: 'customer-company-details-management' })}
                        </strong>{' '}
                        {t('searchingResultTexts.case1.fourthPart', { ns: 'customer-company-details-management' })}
                      </div>
                      <Button
                        sx={baseButtonBlueBg}
                        onClick={handleSearchPaymentsToReviewDataByRegex}
                      >
                        {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                      </Button>
                      {errors.generalError && (
                        <div
                          style={{ marginTop: '0px' }}
                          className="error-message"
                        >
                          {errors.generalError}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        <img
                          src={HelpIcon}
                          alt="help-icon"
                        />
                      </div>
                      <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customer-company-details-management' })}</div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default PaymentsPendingRoutingManagement
